import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  supportDepartmentsService,
  type DepartmentKey,
  type OverrideInput,
} from '@/services/supportDepartmentsService';

export const SUPPORT_DEPARTMENTS_QUERY_KEY = ['support', 'departments'] as const;

export function useSupportDepartments(enabled: boolean) {
  return useQuery({
    queryKey: SUPPORT_DEPARTMENTS_QUERY_KEY,
    queryFn: () => supportDepartmentsService.list(),
    enabled,
    retry: false,
  });
}

function useInvalidateDepartments() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: SUPPORT_DEPARTMENTS_QUERY_KEY });
}

export function useSyncDepartments() {
  const invalidate = useInvalidateDepartments();
  return useMutation({
    mutationFn: () => supportDepartmentsService.sync(),
    onSuccess: invalidate,
  });
}

export function useSetSlackOverride() {
  const invalidate = useInvalidateDepartments();
  return useMutation({
    mutationFn: ({ personId, slackId }: { personId: number; slackId: string | null }) =>
      supportDepartmentsService.setOverride(personId, slackId),
    onSuccess: invalidate,
  });
}

export function useSetEscalation() {
  const invalidate = useInvalidateDepartments();
  return useMutation({
    mutationFn: ({ department, slackId }: { department: DepartmentKey; slackId: string | null }) =>
      supportDepartmentsService.setEscalation(department, slackId),
    onSuccess: invalidate,
  });
}

export function useUpsertDepartmentOverride() {
  const invalidate = useInvalidateDepartments();
  return useMutation({
    mutationFn: ({ department, input }: { department: DepartmentKey; input: OverrideInput }) =>
      supportDepartmentsService.upsertDepartmentOverride(department, input),
    onSuccess: invalidate,
  });
}

export function useDeleteDepartmentOverride() {
  const invalidate = useInvalidateDepartments();
  return useMutation({
    mutationFn: ({ department, email }: { department: DepartmentKey; email: string }) =>
      supportDepartmentsService.deleteDepartmentOverride(department, email),
    onSuccess: invalidate,
  });
}
